import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { getTours } from '../services/api';
import { parseTurkishDateRange, daysUntil } from '../utils/dateParser';
import SEO from './SEO';

const TR_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const TR_DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const buildAllEvents = (tours) => {
  const events = [];
  for (const t of tours || []) {
    if (!t.active) continue;
    for (const pd of t.pricingDates || []) {
      const parsed = parseTurkishDateRange(pd.date);
      if (!parsed?.startDate) continue;
      const capacity = pd.capacity > 0 ? pd.capacity : t.groupSize || 15;
      const remaining = Math.max(0, capacity - (pd.booked || 0));
      events.push({
        tourId: t.id,
        slug: t.slug,
        title: t.title,
        location: t.location,
        levelName: t.levelName,
        currency: t.currency || '₺',
        price: pd.perPerson && pd.perPerson > 0 ? pd.perPerson : t.salePrice,
        rawDate: pd.date,
        startDate: parsed.startDate,
        endDate: parsed.endDate,
        available: pd.available !== false && remaining > 0,
        remaining,
        capacity,
      });
    }
  }
  events.sort((a, b) => a.startDate - b.startDate);
  return events;
};

const monthKey = (year, month) => `${year}-${month}`;

const groupByDay = (events) => {
  const map = new Map();
  for (const ev of events) {
    // For multi-day, mark each day between start & end
    const start = new Date(ev.startDate);
    start.setHours(0, 0, 0, 0);
    const end = ev.endDate ? new Date(ev.endDate) : start;
    end.setHours(0, 0, 0, 0);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
  }
  return map;
};

const CalendarPage = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    getTours({ limit: 100 })
      .then((r) => setTours(r.data || []))
      .catch(() => setTours([]))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  const events = useMemo(() => buildAllEvents(tours), [tours]);
  const byDay = useMemo(() => groupByDay(events), [events]);

  const firstOfMonth = new Date(cursor.year, cursor.month, 1);
  const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
  const firstWeekday = (firstOfMonth.getDay() + 6) % 7; // 0=Mon

  const prevMonth = () => {
    let y = cursor.year, m = cursor.month - 1;
    if (m < 0) { m = 11; y -= 1; }
    setCursor({ year: y, month: m });
    setSelectedKey(null);
  };
  const nextMonth = () => {
    let y = cursor.year, m = cursor.month + 1;
    if (m > 11) { m = 0; y += 1; }
    setCursor({ year: y, month: m });
    setSelectedKey(null);
  };

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const canPrev = !(cursor.year === today.getFullYear() && cursor.month === today.getMonth());

  const selectedEvents = selectedKey ? (byDay.get(selectedKey) || []) : [];

  return (
    <section className="bg-white min-h-screen" data-testid="calendar-page">
      <SEO
        title="Tur Takvimi — Yaklaşan Trekking Tarihleri"
        description="Camption'ın tüm rehberli trekking tur tarihlerini aylık takvim görünümünde keşfedin. Likya Yolu, Karya Yolu ve diğer rotalarda yaklaşan gruplar."
        url="https://camption.com/takvim"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-4">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-[#3D4A26]">Ana Sayfa</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-700">Takvim</li>
          </ol>
        </nav>

        <div className="flex items-end justify-between mb-8 md:mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-2">Yaklaşan Etkinlikler</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1B1B1B] leading-tight" style={{ fontFamily: 'DM Sans, sans-serif' }}>Tur Takvimi</h1>
            <p className="text-gray-600 mt-2 max-w-xl">Tüm tarihleri tek bakışta görün. Bir güne tıklayarak o günün turlarını inceleyin.</p>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500">Yükleniyor...</p>
        ) : (
          <>
            {/* Month header */}
            <div className="flex items-center justify-between mb-4 bg-[#FAFAF7] border border-gray-200 rounded-xl px-4 py-3">
              <button
                type="button"
                onClick={prevMonth}
                disabled={!canPrev}
                data-testid="calendar-prev-month"
                className="p-2 rounded-full hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Önceki ay"
              >
                <ChevronLeft size={20} />
              </button>
              <h2 className="text-xl md:text-2xl font-bold text-[#1B1B1B]" style={{ fontFamily: 'DM Sans, sans-serif' }} data-testid="calendar-month-label">
                {TR_MONTHS[cursor.month]} {cursor.year}
              </h2>
              <button type="button" onClick={nextMonth} data-testid="calendar-next-month" className="p-2 rounded-full hover:bg-white" aria-label="Sonraki ay">
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {TR_DAYS.map((d) => (
                <div key={d} className="text-center text-[11px] font-bold uppercase tracking-widest text-gray-400 py-2">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 md:gap-2">
              {cells.map((day, i) => {
                if (day === null) return <div key={i} className="aspect-square md:aspect-[4/3]" />;
                const cellDate = new Date(cursor.year, cursor.month, day);
                cellDate.setHours(0, 0, 0, 0);
                const isPast = cellDate < today;
                const key = `${cursor.year}-${cursor.month}-${day}`;
                const dayEvents = byDay.get(key) || [];
                const isSelected = selectedKey === key;
                const isToday = cellDate.getTime() === today.getTime();

                const clickable = dayEvents.length > 0;

                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => clickable && setSelectedKey(isSelected ? null : key)}
                    disabled={!clickable}
                    data-testid={`calendar-day-${cursor.year}-${cursor.month + 1}-${day}`}
                    className={`aspect-square md:aspect-[4/3] rounded-lg text-left px-2 py-1.5 relative transition-all border ${
                      isSelected
                        ? 'bg-[#3D4A26] text-white border-[#3D4A26]'
                        : dayEvents.length > 0
                          ? 'bg-white border-[#C8D843]/60 hover:border-[#3D4A26] hover:shadow-sm'
                          : `bg-white border-gray-100 ${isPast ? 'opacity-40' : ''} cursor-default`
                    }`}
                  >
                    <span className={`text-xs md:text-sm font-bold ${isToday && !isSelected ? 'text-[#3D4A26]' : ''}`}>
                      {day}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="hidden md:block mt-1 text-[10px] font-semibold leading-tight line-clamp-2">
                        {dayEvents[0].title}
                        {dayEvents.length > 1 && <span className="block opacity-70">+{dayEvents.length - 1} tur</span>}
                      </span>
                    )}
                    {dayEvents.length > 0 && (
                      <span className={`md:hidden absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-[#3D4A26]'}`} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected day details */}
            {selectedEvents.length > 0 && (
              <div className="mt-8 border-t border-gray-200 pt-6" data-testid="calendar-selected-day">
                <h3 className="text-lg font-bold mb-4">
                  {(() => {
                    const [y, m, d] = selectedKey.split('-').map(Number);
                    const dd = new Date(y, m, d);
                    return `${dd.getDate()} ${TR_MONTHS[dd.getMonth()]} ${dd.getFullYear()}`;
                  })()}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedEvents.map((ev, i) => {
                    const remaining = daysUntil(ev.startDate);
                    return (
                      <Link
                        key={`${ev.tourId}-${i}`}
                        to={`/tur/${ev.slug}`}
                        data-testid={`calendar-event-${ev.tourId}`}
                        className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-[#3D4A26]/50 hover:shadow-sm transition-all"
                      >
                        <p className="font-bold text-[#1B1B1B] mb-1 line-clamp-2">{ev.title}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mb-2"><MapPin size={11} /> {ev.location}</p>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-[#3D4A26] font-semibold">{ev.currency}{new Intl.NumberFormat('tr-TR').format(ev.price)}</span>
                          {remaining !== null && remaining >= 0 && (
                            <span className="text-gray-500">{remaining === 0 ? 'Bugün' : `${remaining} gün kaldı`}</span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {events.length === 0 && (
              <p className="text-center text-gray-500 py-10">Şu anda planlanmış tarih bulunmuyor.</p>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default CalendarPage;
